import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Breadcrumb } from "../Breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { ArrowLeft, Plus, Edit, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

interface Course {
  id: string;
  name: string;
  duration: string;
  department: string;
  students: number;
}

interface CoursesProps {}

export function Courses({}: CoursesProps) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([
    { id: "CS101", name: "BCA", duration: "3 Years", department: "Computer Science", students: 45 },
    { id: "MG201", name: "MBA", duration: "2 Years", department: "Management", students: 35 },
    { id: "BB301", name: "BBA", duration: "3 Years", department: "Business", students: 40 },
    { id: "CS102", name: "MCA", duration: "2 Years", department: "Computer Science", students: 30 },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    duration: "",
    department: "",
  });

  const filteredCourses = courses.filter(
    (course) =>
      course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = () => {
    if (!selectedCourse || !formData.name || !formData.duration || !formData.department) {
      toast.error("Please fill in all fields");
      return;
    }

    setCourses(
      courses.map((course) =>
        course.id === selectedCourse.id
          ? { ...course, name: formData.name, duration: formData.duration, department: formData.department }
          : course
      )
    );

    setIsEditing(false);
    setSelectedCourse(null);
    setFormData({ name: "", duration: "", department: "" });
    toast.success("Course updated successfully!");
  };

  const handleDelete = () => {
    if (selectedCourse) {
      setCourses(courses.filter((course) => course.id !== selectedCourse.id));
      setDeleteDialogOpen(false);
      setSelectedCourse(null);
      toast.success("Course removed successfully!");
    }
  };

  const openEditDialog = (course: Course) => {
    setSelectedCourse(course);
    setFormData({
      name: course.name,
      duration: course.duration,
      department: course.department,
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setSelectedCourse(null);
    setFormData({ name: "", duration: "", department: "" });
  };

  const openDeleteDialog = (course: Course) => {
    setSelectedCourse(course);
    setDeleteDialogOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Header */}
          {isEditing && selectedCourse ? (
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Edit Course
                </h1>

                <Breadcrumb
                  items={[
                    { label: "Courses", href: "/edit-courses" },
                    { label: "Edit" },
                  ]}
                  className="mt-2"
                />
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={cancelEdit}
                className="rounded-full shadow-sm hover:bg-muted transition"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Courses
                </h1>

                <Breadcrumb items={[{ label: "Courses" }]} className="mt-2" />
              </div>

              <Button
                onClick={() => navigate(PATHS.ADD_COURSE)}
                className="bg-primary hover:bg-primary/90 text-white transition"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="text-sm font-bold">Add Course</span>
              </Button>

            </div>
          )}

          {/* SEARCH */}
          {!isEditing && (
            <Card className="bg-card border border-white/10 shadow-sm">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                    <Input
                      placeholder="Search courses..."
                      className="pl-10 bg-background border-white/5 text-sm font-medium"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />

                </div>
              </CardContent>
            </Card>
          )}

          {/* TABLE */}
          {!isEditing && (
            <Card className="bg-card border border-white/10 shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">
                  All Courses ({filteredCourses.length})
                </CardTitle>

              </CardHeader>

              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Course ID</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Course Name</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Duration</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Department</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Students</TableHead>
                      <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 pr-4">Actions</TableHead>
                    </TableRow>

                  </TableHeader>

                  <TableBody>
                    {filteredCourses.map((course) => (
                      <TableRow
                        key={course.id}
                        className="hover:bg-muted/40 transition"
                      >
                        <TableCell className="text-[13px] font-mono text-slate-500">
                          {course.id}
                        </TableCell>

                        <TableCell>
                          <Badge variant="outline" className="text-[11px] font-bold uppercase tracking-widest px-2.5 py-0.5 border-slate-200 dark:border-white/10">
                            {course.name}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-[13px] font-medium text-slate-600 dark:text-slate-400">{course.duration}</TableCell>
                        <TableCell className="text-[13px] font-medium text-slate-600 dark:text-slate-400">{course.department}</TableCell>
                        <TableCell className="text-[13px] font-bold text-slate-900 dark:text-white">{course.students}</TableCell>


                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">

                            {/* EDIT */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(course)}
                                  className="hover:bg-muted transition"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Edit Course
                              </TooltipContent>
                            </Tooltip>

                            {/* DELETE */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openDeleteDialog(course)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 transition"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Delete Course
                              </TooltipContent>
                            </Tooltip>

                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* DELETE DIALOG */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent className="bg-card border border-white/10 shadow-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Are you sure?
                </AlertDialogTitle>

                <AlertDialogDescription>
                  This will permanently delete the course{" "}
                  <strong>{selectedCourse?.name}</strong>.
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>
                  Cancel
                </AlertDialogCancel>

                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </div>
      </div>
    </TooltipProvider>
  );
}